import { useMemo } from "react";

export const MAX_WEIGHT_LB = 150;
export const MAX_WEIGHT_KG = 68;
export const MAX_DIMENSION_IN = 108;
export const MAX_DIMENSION_CM = 274;

export interface PackageValidationInput {
  weight?: number | string | null;
  length?: number | string | null;
  width?: number | string | null;
  height?: number | string | null;
  unitSystem?: "imperial" | "metric" | string;
}

export interface PackageValidationErrors {
  weight?: string | null;
  length?: string | null;
  width?: string | null;
  height?: string | null;
}

export const validateWeightValue = (
  weight: number | string | undefined | null,
  unitSystem?: string
): string | null => {
  if (weight === undefined || weight === null || weight === "") {
    return "El peso debe ser mayor a 0";
  }
  const numWeight = Number(weight);
  if (isNaN(numWeight) || numWeight <= 0) {
    return "El peso debe ser mayor a 0";
  }

  const isMetric = String(unitSystem).toLowerCase() === "metric";
  if (isMetric) {
    if (numWeight > MAX_WEIGHT_KG) {
      return `El peso por paquete no puede ser mayor a ${MAX_WEIGHT_KG} kg.`;
    }
  } else {
    if (numWeight > MAX_WEIGHT_LB) {
      return `El peso por paquete no puede ser mayor a ${MAX_WEIGHT_LB} lb.`;
    }
  }

  return null;
};

export const validateDimensionValue = (
  val: number | string | undefined | null,
  dimLabel: string,
  unitSystem?: string
): string | null => {
  if (val === undefined || val === null || val === "") {
    return `${dimLabel} debe ser mayor a 0`;
  }
  const numVal = Number(val);
  if (isNaN(numVal) || numVal <= 0) {
    return `${dimLabel} debe ser mayor a 0`;
  }

  const isMetric = String(unitSystem).toLowerCase() === "metric";
  if (isMetric) {
    if (numVal > MAX_DIMENSION_CM) {
      return `El ${dimLabel.toLowerCase()} no puede superar los ${MAX_DIMENSION_CM} cm.`;
    }
  } else {
    if (numVal > MAX_DIMENSION_IN) {
      return `El ${dimLabel.toLowerCase()} no puede superar las ${MAX_DIMENSION_IN} in.`;
    }
  }

  return null;
};

export const usePackageValidation = () => {
  const validateWeight = (
    weight: number | string | undefined | null,
    unitSystem?: string
  ) => validateWeightValue(weight, unitSystem);

  const validateDimension = (
    val: number | string | undefined | null,
    dimLabel: string,
    unitSystem?: string
  ) => validateDimensionValue(val, dimLabel, unitSystem);

  const validatePackage = (input: PackageValidationInput) => {
    const unitSystem = input.unitSystem || "imperial";
    const weightErr = validateWeightValue(input.weight, unitSystem);
    const lengthErr = validateDimensionValue(input.length, "Largo", unitSystem);
    const widthErr = validateDimensionValue(input.width, "Ancho", unitSystem);
    const heightErr = validateDimensionValue(input.height, "Alto", unitSystem);

    const isValid = !weightErr && !lengthErr && !widthErr && !heightErr;

    return {
      errors: {
        weight: weightErr,
        length: lengthErr,
        width: widthErr,
        height: heightErr,
      } as PackageValidationErrors,
      isValid,
    };
  };

  return useMemo(
    () => ({
      validateWeight,
      validateDimension,
      validatePackage,
      MAX_WEIGHT_LB,
      MAX_WEIGHT_KG,
      MAX_DIMENSION_IN,
      MAX_DIMENSION_CM,
    }),
    []
  );
};

export default usePackageValidation;
